/**
 * Shared authentication external store.
 *
 * Litopia and embedded OpenPenPal consume one Supabase auth subscription.
 * Hook instances only subscribe to this store, avoiding duplicate GoTrue
 * listeners and recovering when the host registers its client after import.
 */
import { useCallback, useSyncExternalStore } from 'react';
import type { AuthChangeEvent, Session, SupabaseClient, User } from '@supabase/supabase-js';
import { getSupabaseOrNull, subscribeSupabaseClient } from '../supabase';

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, metadata?: Record<string, unknown>) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

type AuthSnapshot = Pick<AuthState, 'user' | 'session' | 'loading'>;

let snapshot: AuthSnapshot = { user: null, session: null, loading: true };
let activeClient: SupabaseClient | null = null;
let unsubscribeAuth: (() => void) | null = null;
const listeners = new Set<() => void>();

function emit(next: AuthSnapshot) {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

function applySession(session: Session | null) {
  emit({ session, user: session?.user ?? null, loading: false });
}

function syncAuthClient() {
  const client = getSupabaseOrNull();
  if (client === activeClient) return;

  unsubscribeAuth?.();
  unsubscribeAuth = null;
  activeClient = client;

  if (!client) {
    emit({ user: null, session: null, loading: false });
    return;
  }

  emit({ ...snapshot, loading: true });
  const clientAtRequest = client;
  void client.auth.getSession()
    .then(({ data: { session } }) => {
      if (activeClient === clientAtRequest) applySession(session);
    })
    .catch(() => {
      if (activeClient === clientAtRequest) applySession(null);
    });

  const { data: { subscription } } = client.auth.onAuthStateChange(
    (_event: AuthChangeEvent, session: Session | null) => {
      if (activeClient === clientAtRequest) applySession(session);
    },
  );
  unsubscribeAuth = () => subscription.unsubscribe();
}

// The registry subscription is singleton-scoped for the lifetime of the app.
subscribeSupabaseClient(syncAuthClient);
syncAuthClient();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return snapshot;
}

function clientUnavailableError(action: string) {
  return new Error(`Supabase client not initialized for ${action}`);
}

export function useSharedAuth(): AuthState {
  const auth = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const signIn = useCallback(async (email: string, password: string) => {
    const client = getSupabaseOrNull();
    if (!client) return { error: clientUnavailableError('signIn') };
    const { error } = await client.auth.signInWithPassword({ email, password });
    return { error: error ? new Error(error.message) : null };
  }, []);

  const signUp = useCallback(async (
    email: string,
    password: string,
    metadata?: Record<string, unknown>,
  ) => {
    const client = getSupabaseOrNull();
    if (!client) return { error: clientUnavailableError('signUp') };
    const { error } = await client.auth.signUp({
      email,
      password,
      options: { data: metadata },
    });
    return { error: error ? new Error(error.message) : null };
  }, []);

  const signOut = useCallback(async () => {
    const client = getSupabaseOrNull();
    if (!client) return;
    const { error } = await client.auth.signOut();
    if (error) throw new Error(error.message);
  }, []);

  return { ...auth, signIn, signUp, signOut };
}
